import b from 'b_';
import { inject, observer } from 'mobx-react';
import React, { Component } from 'react';
import ConsoleStore from '../../stores/ConsoleStore';
import { USER_GROUPS_SOURCES } from '../../stores/Policy/BrowsingRoles/BrowsingRolesStore';
import PolicyStore from '../../stores/Policy/PolicyStore';
import SnackStore from '../../stores/SnackStore';
import FuseConsoleCardFooter from '../ConsoleFragments/FuseConsoleCardFooter';
import FuseConsoleModal from '../ConsoleModal/FuseConsoleModal';
import { Card, FTHeading, Field } from '../Fuse';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseButton from '../FuseControls/FuseButton';
import FuseReadOnlyRadioButton from '../FuseControls/FuseReadOnlyRadioButton';
import UserGroupSourceLabel from './UserGroupSourceLabel';
import { ROLE_READ_ONLY } from '../../stores/Administrators/AdministratorModalStore';
import './UserGroupsSources.css';

const block = b.lock('UserGroupsSources');

class UserGroupsSources extends Component {
    static propTypes = {
        snack: SnackStore.PropType,
        console: ConsoleStore.PropType,
        policy: PolicyStore.PropType.isRequired,
    };

    onSaveClick = () => {
        const { console, policy } = this.props;

        policy.browsingRoles.update(console.customerId)
            .then(() => this.props.snack.showSuccess('Group source updated.'))
            .catch((e) => e && this.props.snack.showError(`Failed to update group source - ${e}`));
    };

    renderModal() {
        const { policy } = this.props;
        const {
            updateStatus,
            userGroupsSourceForm,
            userGroupSourceModal,
            allowedUserGroupsSources,
        } = policy.browsingRoles;

        return <FuseConsoleModal
            isOpen={userGroupSourceModal.isOpen}
            onClose={userGroupSourceModal.close}
            title="Edit Browsing Roles"
            subTitle="- User Group Source"
            className={block()}
            headerActions={<FuseButton
                color={'green'}
                style={{ minWidth: 100 }}
                disabled={!userGroupsSourceForm.hasChanged()}
                onClick={this.onSaveClick}
                loading={updateStatus.isLoading}
            >Save</FuseButton>}
        >
            <Card>
                <FuseAlert
                    icon={'warning'}
                    title={'Warning'}
                    subtitle={`- Making changes to this section will impact existing
                    Browsing Roles that reference previously synchronized user groups.`}
                />
                <FTHeading className={block('ModalHeading')}>User Group Source</FTHeading>
                {this.renderOptions()}
                <div>
                    {!allowedUserGroupsSources[USER_GROUPS_SOURCES.SAML] ? <FuseConsoleCardFooter
                        className={block('ModalFooterNote')}
                        text={'SAML/SSO must be configured to support IdP directory synchronization.'}
                    /> : null}
                    {!allowedUserGroupsSources[USER_GROUPS_SOURCES.TAP] ? <FuseConsoleCardFooter
                        className={block('ModalFooterNote')}
                        text={'TAP/PPS requires TAP integration to support group synchronization.'}
                    /> : null}
                </div>
            </Card>
        </FuseConsoleModal>;
    }

    renderOptions({ readOnly } = {}) {
        const { console } = this.props;
        const { userGroupsSource, userGroupsSourceForm, allowedUserGroupsSources } = this.props.policy.browsingRoles;
        const st = readOnly ? userGroupsSource : userGroupsSourceForm;
        const hasGroups = console.hasUrlIsolation() || console.isSSOConfigured;

        return <Field label="Source Type">
            {Object.values(USER_GROUPS_SOURCES).map((value) =>
                <FuseReadOnlyRadioButton
                    value={value}
                    key={value}
                    disabled={!allowedUserGroupsSources[value]}
                    label={<UserGroupSourceLabel
                        /* replace text for "None" when user has no groups available */
                        value={value === USER_GROUPS_SOURCES.NONE && !hasGroups ? 'noGroups' : value}
                    />}
                    readOnly={readOnly}
                    store={st}
                    qa={`source-switch-${value}`}
                />)}
        </Field>;
    }

    render() {
        const { policy, console } = this.props;
        const { loadStatus, openEditModal, policyTable } = policy.browsingRoles;
        const { userGroups } = policyTable.policyModal.sections;
        const lastSyncGroupText =
            userGroups.lastSyncGroupDate ? `Last Synchronization: ${userGroups.lastSyncGroupDate}` : '';

        return <Card
            title="Browsing Roles - User Group Source"
            titleAction={console.authority === ROLE_READ_ONLY.value ? null : <FuseButton
                color="blue"
                icon="edit"
                onClick={openEditModal}
                loading={loadStatus.isLoading}
                qa="edit-user-groups"
            >Edit</FuseButton>}
            loading={loadStatus.isLoading}
        >
            {console.hasUrlIsolation() || console.isSSOConfigured
                ? this.renderOptions({ readOnly: true })
                : <Field label="Source Type">
                    <b>Disabled</b> - User Group Synchronization is only available if
                    your service includes TAP URL Isolation or if it is configured to provide SSO
                </Field>}

            {lastSyncGroupText ? <FuseConsoleCardFooter text={lastSyncGroupText} /> : null}

            {this.renderModal()}
        </Card>;
    }
}

export default inject('policy', 'console', 'snack')(observer(UserGroupsSources));
